import {
  ChangeDetectionStrategy,
  Component,
  input,
  inject,
  signal,
  computed,
  OnInit,
  DestroyRef,
  effect,
} from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import {
  CompoundArticleDetail,
  WarehouseProductionCapacity,
  ComponentWarehouseSourceInput,
  WarehouseCompoundArticle,
  ComponentSource,
  AvailableUnit,
} from '@shared-models';
import { CompoundArticlesService } from 'src/app/@pages/inventory/components/compound-articles/services/compound-articles.service';
import { WarehouseService } from 'src/app/@pages/warehouse/warehouse.service';
import { UnitConversionService } from '../../../../services/unit-conversion.service';
import { AlertsService } from '../../../services/alerts/alerts.service';
import { Router } from '@angular/router';
import { IUnits } from 'src/app/@interfaces/units.interface';

@Component({
  selector: 'app-produce-compound-article',
  imports: [
    CommonModule,
    DecimalPipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatSelectModule,
    MatInputModule,
    MatFormFieldModule,
    MatTableModule,
    MatIconModule,
    MatDialogModule,
    MatCardModule,
    TranslatePipe,
  ],
  templateUrl: './produce-compound-article.component.html',
  styleUrl: './produce-compound-article.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProduceCompoundArticleComponent implements OnInit {
  private readonly compoundArticlesService = inject(CompoundArticlesService);
  private readonly warehouseService = inject(WarehouseService);
  private readonly unitConversionService = inject(UnitConversionService);
  private readonly translateService = inject(TranslateService);
  private readonly dialogRef = inject(
    MatDialogRef<ProduceCompoundArticleComponent>,
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly alertsService = inject(AlertsService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  // Inputs
  compoundArticleId = input.required<string>();

  // Signals for reactive state
  compoundArticle = signal<CompoundArticleDetail | null>(null);
  allWarehouses = signal<WarehouseCompoundArticle[]>([]);
  allUnits = signal<IUnits[]>([]);
  selectedWarehouse = signal<number | null>(null);
  quantityToProduce = signal<number>(1);
  componentSources = signal<ComponentSource[]>([]);
  productionCapacity = signal<WarehouseProductionCapacity | null>(null);
  isLoading = signal<boolean>(true);
  isProducing = signal<boolean>(false);
  hasError = signal<boolean>(false);
  errorMessage = signal<string>('');
  private isUpdatingStockInfo = false;

  // Signal for FormControl state to make it reactive
  private formControlState = signal<{ valid: boolean; value: number | null }>({
    valid: false,
    value: null,
  });

  // Form controls
  quantityControl = new FormControl<number>(1, [
    Validators.required,
    Validators.min(1),
    Validators.pattern(/^\d+$/), // Solo enteros
  ]);

  // Computed signals
  isFormValid = computed(() => {
    const quantity = this.quantityToProduce();
    const capacity = this.productionCapacity();
    const hasSelectedWarehouse = this.selectedWarehouse() !== null;
    const formControlState = this.formControlState(); // Now it's reactive!
    const isFormControlValid =
      formControlState.valid && formControlState.value !== null;
    const hasEnoughCapacity =
      capacity && capacity.max_production_quantity > 0
        ? quantity <= capacity.max_production_quantity
        : false;
    const isNotLoading = !this.isLoading();

    return (
      hasSelectedWarehouse &&
      isFormControlValid &&
      hasEnoughCapacity &&
      isNotLoading
    );
  });

  showWarehouseWarning = computed(() => {
    const mainWarehouse = this.selectedWarehouse();
    const sources = this.componentSources();

    return sources.some((source) => source.id_warehouse !== mainWarehouse);
  });

  maxQuantityAvailable = computed(() => {
    const capacity = this.productionCapacity();
    // Return null when no capacity is calculated yet to avoid max="0" validation
    return capacity ? capacity.max_production_quantity : null;
  });

  // New computed signals for recipe quantity logic
  shouldShowRecipeInfo = computed(() => {
    const recipe = this.compoundArticle();
    return recipe?.quantity && recipe.quantity > 1;
  });

  totalProductionQuantity = computed(() => {
    const quantity = this.quantityToProduce();
    const recipeQuantity = this.compoundArticle()?.quantity || 1;
    return quantity * recipeQuantity;
  });

  recipeQuantity = computed(() => {
    return this.compoundArticle()?.quantity || 1;
  });

  // Dynamic labels for batch vs unit terminology
  quantityLabel = computed(() => {
    return this.shouldShowRecipeInfo()
      ? 'inventory.compoundArticles.modals.produce.batchesToProduce'
      : 'inventory.compoundArticles.modals.produce.quantityToProduce';
  });

  quantityUnit = computed(() => {
    return this.shouldShowRecipeInfo()
      ? 'inventory.compoundArticles.modals.produce.batches'
      : 'inventory.compoundArticles.modals.produce.units';
  });

  maxAvailableHintKey = computed(() => {
    return this.shouldShowRecipeInfo()
      ? 'inventory.compoundArticles.modals.produce.maxAvailableBatchHint'
      : 'inventory.compoundArticles.modals.produce.maxAvailableHint';
  });

  displayedColumns = [
    'description',
    'unit',
    'availableStock',
    'requiredStock',
    'sourceWarehouse',
  ];

  // Helper method for template
  parseInt = parseInt;

  constructor() {
    // Update both FormControl state signal AND quantity signal when FormControl changes
    this.quantityControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.formControlState.set({
          valid: this.quantityControl.valid,
          value: this.quantityControl.value,
        });

        this.quantityToProduce.set(value ? value : 0);
      });

    // Also update on status changes (when validation changes)
    this.quantityControl.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.formControlState.set({
          valid: this.quantityControl.valid,
          value: this.quantityControl.value,
        });
      });

    // Recalculate capacity when warehouse selection changes
    effect(() => {
      const selectedWarehouse = this.selectedWarehouse();
      const article = this.compoundArticle();
      const isLoading = this.isLoading();

      // Only recalculate if warehouse, article exist and not loading
      if (selectedWarehouse && article && !isLoading) {
        // Call in next cycle to avoid loops
        this.triggerCapacityCalculation();
      }
    });
  }

  ngOnInit() {
    this.initializeModal();
  }

  private async initializeModal() {
    try {
      this.isLoading.set(true);

      // First, fetch the compound article data
      const compoundArticleId = this.compoundArticleId();

      this.compoundArticlesService
        .getCompoundArticle(compoundArticleId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (compoundArticle) => {
            this.compoundArticle.set(compoundArticle);

            // Then load warehouses
            this.loadWarehouses();
          },
          error: (error) => {
            this.handleError(
              this.translateService.instant(
                'inventory.compoundArticles.modals.produce.errors.loadingCompoundArticle',
              ),
              error,
            );
          },
        });
    } catch (error) {
      this.handleError(
        this.translateService.instant(
          'inventory.compoundArticles.modals.produce.errors.initializingModal',
        ),
        error,
      );
    }
  }

  private loadWarehouses() {
    this.warehouseService
      .listWarehouses()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (warehouses) => {
          this.allWarehouses.set(warehouses);

          // Select first warehouse as default
          if (warehouses.length > 0) {
            this.selectedWarehouse.set(parseInt(warehouses[0].id_almacen));
          }

          this.initializeComponentSources();
        },
        error: (error) => {
          this.handleError(
            this.translateService.instant(
              'inventory.compoundArticles.modals.produce.errors.loadingWarehouses',
            ),
            error,
          );
        },
      });
  }

  private initializeComponentSources() {
    const article = this.compoundArticle();
    const selectedMainWarehouse = this.selectedWarehouse();

    if (!article || !article?.baseArticle?.almacen || !article?.subArticles)
      return;

    const sources: ComponentSource[] = article.subArticles
      .map((subArticle) => {
        const availableWarehouses = subArticle.baseArticle
          .almacen as unknown as WarehouseCompoundArticle[];

        if (!availableWarehouses) {
          return null;
        }

        // Auto-select warehouse logic
        let defaultWarehouse = parseInt(availableWarehouses[0]?.id_almacen);

        // Try to match main warehouse selection if available
        if (selectedMainWarehouse) {
          const matchingWarehouse = availableWarehouses.find(
            (w) => parseInt(w.id_almacen) === selectedMainWarehouse,
          );

          if (matchingWarehouse) {
            defaultWarehouse = parseInt(matchingWarehouse.id_almacen);
          }
        }

        // Get base unit from the FORMULA (compound_article_components relationship)
        // This is the unit specified in the recipe, not the article's default unit
        // Priority: subArticle.id_unit (formula unit) > baseArticle.unit.id_unit > baseArticle.id_unit
        const baseUnitId =
          subArticle.id_unit ||
          subArticle.baseArticle?.unit?.id_unit ||
          subArticle.baseArticle?.id_unit ||
          0;
        // Get the unit description from the FORMULA unit (subArticle.unit), not the article's default unit
        const baseUnitDescription =
          subArticle.unit?.description || // First priority: formula unit description
          subArticle.baseArticle?.unit?.description ||
          subArticle.baseArticle?.unidad ||
          '';

        // Initialize with base unit as the only available unit (will be updated async)
        const baseUnit: AvailableUnit = {
          id_unit: baseUnitId,
          description: baseUnitDescription,
          conversionFactor: 1,
        };

        return {
          id_component_article: subArticle.id_component_article,
          id_warehouse: defaultWarehouse,
          availableWarehouses,
          requiredQuantity: 0,
          availableStock: 0,
          articleDescription:
            `${subArticle.baseArticle?.descripcion} - ${subArticle.baseArticle.codigo}` ||
            '',
          baseUnitId,
          selectedUnitId: baseUnitId,
          availableUnits: [baseUnit],
          originalRequiredQuantity: 0,
          originalAvailableStock: 0,
          isLoadingUnits: true,
        };
      })
      .filter((source): source is ComponentSource => source !== null);

    this.componentSources.set(sources);
    this.isLoading.set(false);

    // Load all available units
    this.loadAvailableUnits();

    // Calculate initial capacity after initializing sources
    this.calculateProductionCapacity();
  }

  private loadAvailableUnits() {
    const currentSources = this.componentSources();

    console.log(
      '[loadAvailableUnits] ========================================',
    );
    console.log(
      '[loadAvailableUnits] Loading available units for each component',
    );
    console.log(
      '[loadAvailableUnits] Total components:',
      currentSources.length,
    );

    // For each component, load available units that have conversions registered
    currentSources.forEach((source) => {
      if (source.baseUnitId === 0) {
        console.warn(
          '[loadAvailableUnits] Component has invalid baseUnitId:',
          source.id_component_article,
        );
        return;
      }

      console.log(
        '[loadAvailableUnits] ----------------------------------------',
      );
      console.log('[loadAvailableUnits] Component:', source.articleDescription);
      console.log('[loadAvailableUnits] Base Unit ID:', source.baseUnitId);

      this.unitConversionService
        .getAvailableConversionsFromUnit(source.baseUnitId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (conversions) => {
            console.log(
              '[loadAvailableUnits] ✓ API Response - Conversions:',
              conversions.length,
            );

            // Map conversions to AvailableUnit format
            const availableUnits: AvailableUnit[] = conversions.map((conv) => ({
              id_unit: conv.unit.id_unit,
              description: conv.unit.description,
              conversionFactor: conv.conversion_factor,
            }));

            // Always include the base unit at the beginning (with factor 1)
            const baseUnit: AvailableUnit = {
              id_unit: source.baseUnitId,
              description: source.availableUnits[0]?.description || '',
              conversionFactor: 1,
            };

            // Check if base unit is already in the list
            const baseUnitExists = availableUnits.some(
              (u) => u.id_unit === source.baseUnitId,
            );

            // Build final list: base unit first, then converted units
            const finalUnits: AvailableUnit[] = baseUnitExists
              ? availableUnits
              : [baseUnit, ...availableUnits];

            console.log(
              '[loadAvailableUnits] Available units for component:',
              finalUnits.length,
            );
            console.log(
              '[loadAvailableUnits] Units:',
              finalUnits.map((u) => `${u.description} (ID: ${u.id_unit})`),
            );

            // Update this specific component with its available units
            const updatedSources = this.componentSources().map((s) =>
              s.id_component_article === source.id_component_article
                ? {
                    ...s,
                    availableUnits: finalUnits,
                    isLoadingUnits: false,
                  }
                : s,
            );

            this.componentSources.set(updatedSources);
            console.log(
              '[loadAvailableUnits] ✓ Component updated successfully',
            );
          },
          error: (error) => {
            console.error(
              '[loadAvailableUnits] ✗ Error loading conversions for component:',
              source.id_component_article,
            );
            console.error('[loadAvailableUnits] Error:', error);

            // On error, keep only the base unit for this component
            const baseUnit: AvailableUnit = {
              id_unit: source.baseUnitId,
              description: source.availableUnits[0]?.description || '',
              conversionFactor: 1,
            };

            const updatedSources = this.componentSources().map((s) =>
              s.id_component_article === source.id_component_article
                ? {
                    ...s,
                    availableUnits: [baseUnit],
                    isLoadingUnits: false,
                  }
                : s,
            );

            this.componentSources.set(updatedSources);
          },
        });
    });
  }

  private triggerCapacityCalculation() {
    // Use setTimeout to avoid reading signals inside effect
    // DO NOT remove this, it will cause infinite loops
    setTimeout(() => {
      const sourcesLength = this.componentSources().length;
      const isUpdating = this.isUpdatingStockInfo;

      if (sourcesLength > 0 && !isUpdating) {
        this.calculateProductionCapacity();
      }
    }, 0);
  }

  private calculateProductionCapacity() {
    // Skip calculation if updating stock information
    if (this.isUpdatingStockInfo) {
      return;
    }

    const article = this.compoundArticle();
    const selectedWarehouse = this.selectedWarehouse();
    const sources = this.componentSources();

    if (!article || !selectedWarehouse || !sources.length) {
      return;
    }

    const componentSourcesInput: ComponentWarehouseSourceInput[] = sources.map(
      (source) => {
        const baseInput: ComponentWarehouseSourceInput = {
          id_warehouse: source.id_warehouse,
          id_component_article: source.id_component_article,
        };

        // If user has selected a different unit, include it in the request
        // This helps the backend return quantities in the selected unit
        if (source.selectedUnitId !== source.baseUnitId) {
          baseInput.id_unit = source.selectedUnitId;
        }

        return baseInput;
      },
    );

    this.compoundArticlesService
      .getCompoundArticleProductionCapacity(
        article.id_compound_article,
        selectedWarehouse,
        componentSourcesInput,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (capacity) => {
          this.productionCapacity.set(capacity);
          this.isUpdatingStockInfo = true;
          this.updateComponentStockInfo(capacity);
          this.isUpdatingStockInfo = false;
        },
        error: (error) => {
          this.handleError(
            this.translateService.instant(
              'inventory.compoundArticles.modals.produce.errors.calculatingProductionCapacity',
            ),
            error,
          );
        },
      });
  }

  private updateComponentStockInfo(capacity: WarehouseProductionCapacity) {
    const currentSources = this.componentSources();

    const updatedSources = currentSources.map((source) => {
      const stockDetail = capacity.component_stock_details.find(
        (detail) => detail.id_component_article === source.id_component_article,
      );

      const newAvailableStock = stockDetail?.available_stock || 0;
      const newRequiredQuantity =
        stockDetail?.required_quantity_per_unit || source.requiredQuantity;

      const updated = {
        ...source,
        availableStock: newAvailableStock,
        requiredQuantity: newRequiredQuantity,
        originalAvailableStock: newAvailableStock,
        originalRequiredQuantity: newRequiredQuantity,
      };

      return updated;
    });

    this.componentSources.set(updatedSources);
  }

  onWarehouseChange(warehouseId: number) {
    this.selectedWarehouse.set(warehouseId);
    // Effect will handle selectedWarehouse changes automatically
  }

  onComponentWarehouseChange(componentArticleId: number, warehouseId: number) {
    const currentSources = this.componentSources();
    const updatedSources = currentSources.map((source) =>
      source.id_component_article === componentArticleId
        ? { ...source, id_warehouse: warehouseId }
        : source,
    );

    this.componentSources.set(updatedSources);

    // Recalculate capacity after component warehouse change
    this.triggerCapacityCalculation();
  }

  onUnitChange(componentArticleId: number, newUnitId: number) {
    const currentSources = this.componentSources();
    const sourceIndex = currentSources.findIndex(
      (s) => s.id_component_article === componentArticleId,
    );

    if (sourceIndex === -1) {
      console.error('[onUnitChange] Component not found:', componentArticleId);
      return;
    }

    const source = currentSources[sourceIndex];

    console.log('========================================');
    console.log('[onUnitChange] UNIT CHANGE REQUEST');
    console.log('========================================');
    console.log('[onUnitChange] Component ID:', componentArticleId);
    console.log(
      '[onUnitChange] Component Description:',
      source.articleDescription,
    );
    console.log(
      '[onUnitChange] Current selectedUnitId:',
      source.selectedUnitId,
    );
    console.log('[onUnitChange] New unitId:', newUnitId);
    console.log('[onUnitChange] Base unitId:', source.baseUnitId);
    console.log('[onUnitChange] Available units:', source.availableUnits);
    console.log('[onUnitChange] Original values:', {
      availableStock: source.originalAvailableStock,
      requiredQuantity: source.originalRequiredQuantity,
    });
    console.log('========================================');

    // If selecting the same unit as currently selected, do nothing
    if (newUnitId === source.selectedUnitId) {
      console.log('[onUnitChange] Same unit selected, skipping');
      return;
    }

    // If selecting the base unit, restore original values
    if (newUnitId === source.baseUnitId && source.baseUnitId !== 0) {
      console.log('[onUnitChange] Restoring to base unit values');
      const updatedSources = currentSources.map((s) =>
        s.id_component_article === componentArticleId
          ? {
              ...s,
              selectedUnitId: newUnitId,
              availableStock: s.originalAvailableStock,
              requiredQuantity: s.originalRequiredQuantity,
            }
          : s,
      );
      this.componentSources.set(updatedSources);
      return;
    }

    // Determine the fromUnit - use baseUnitId if valid, otherwise use current selectedUnitId
    const fromUnitId =
      source.baseUnitId !== 0 ? source.baseUnitId : source.selectedUnitId;

    const fromUnitDescription =
      source.availableUnits.find((u) => u.id_unit === fromUnitId)
        ?.description || 'unknown';
    const toUnitDescription =
      source.availableUnits.find((u) => u.id_unit === newUnitId)?.description ||
      'unknown';

    console.log('[onUnitChange] >>> CALLING CONVERSION API <<<');
    console.log(
      '[onUnitChange] From Unit ID:',
      fromUnitId,
      '(' + fromUnitDescription + ')',
    );
    console.log(
      '[onUnitChange] To Unit ID:',
      newUnitId,
      '(' + toUnitDescription + ')',
    );

    // Call the API to get the conversion factor between base unit and selected unit
    this.unitConversionService
      .getConversionFactor(fromUnitId, newUnitId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (conversionFactor) => {
          console.log(
            '[onUnitChange] ✓ API RESPONSE - Conversion factor:',
            conversionFactor,
          );
          console.log('[onUnitChange] Response type:', typeof conversionFactor);

          if (conversionFactor === null || conversionFactor === undefined) {
            console.warn('[onUnitChange] ⚠️ NO CONVERSION AVAILABLE');
            console.warn('[onUnitChange] Backend returned:', conversionFactor);
            console.warn('[onUnitChange] Reverting to base unit...');

            // Show alert to the user
            const baseUnitDescription =
              source.availableUnits.find((u) => u.id_unit === source.baseUnitId)
                ?.description || 'base';
            const newUnitDescription =
              source.availableUnits.find((u) => u.id_unit === newUnitId)
                ?.description || 'selected';

            console.warn(
              '[onUnitChange] Showing alert - From:',
              baseUnitDescription,
              'To:',
              newUnitDescription,
            );

            this.alertsService.showInfoAlert(
              this.translateService.instant(
                'inventory.compoundArticles.modals.produce.errors.noConversionAvailable',
                {
                  fromUnit: baseUnitDescription,
                  toUnit: newUnitDescription,
                },
              ),
              '',
            );

            // Revert to base unit - keep current values (no conversion)
            const updatedSources = this.componentSources().map((s) =>
              s.id_component_article === componentArticleId
                ? {
                    ...s,
                    selectedUnitId: s.baseUnitId,
                    availableStock: s.originalAvailableStock,
                    requiredQuantity: s.originalRequiredQuantity,
                  }
                : s,
            );
            this.componentSources.set(updatedSources);
            return;
          }

          // Convert both quantities using the conversion factor
          // When converting FROM base unit TO selected unit, we multiply by the factor
          const convertedAvailableStock =
            source.originalAvailableStock * conversionFactor;
          const convertedRequiredQuantity =
            source.originalRequiredQuantity * conversionFactor;

          console.log('[onUnitChange] ✓ CONVERSION SUCCESS');
          console.log(
            '[onUnitChange] Conversion factor used:',
            conversionFactor,
          );
          console.log('[onUnitChange] Original -> Converted values:', {
            availableStock:
              source.originalAvailableStock + ' -> ' + convertedAvailableStock,
            requiredQuantity:
              source.originalRequiredQuantity +
              ' -> ' +
              convertedRequiredQuantity,
          });

          const updatedSources = this.componentSources().map((s) =>
            s.id_component_article === componentArticleId
              ? {
                  ...s,
                  selectedUnitId: newUnitId,
                  availableStock: convertedAvailableStock,
                  requiredQuantity: convertedRequiredQuantity,
                }
              : s,
          );

          this.componentSources.set(updatedSources);
          console.log('[onUnitChange] Component sources updated successfully');
          console.log('========================================');
        },
        error: (error) => {
          console.error('[onUnitChange] ✗ API ERROR');
          console.error('[onUnitChange] Error details:', error);
          console.error(
            '[onUnitChange] Error message:',
            error?.message || 'Unknown error',
          );

          // Show alert to the user
          const baseUnitDescription =
            source.availableUnits.find((u) => u.id_unit === source.baseUnitId)
              ?.description || 'base';
          const newUnitDescription =
            source.availableUnits.find((u) => u.id_unit === newUnitId)
              ?.description || 'selected';

          this.alertsService.showErrorAlert(
            this.translateService.instant(
              'inventory.compoundArticles.modals.produce.errors.conversionError',
              {
                fromUnit: baseUnitDescription,
                toUnit: newUnitDescription,
              },
            ),
          );

          console.error(
            '[onUnitChange] Reverting to base unit due to API error',
          );

          // Revert to base unit - keep current values (no conversion)
          const updatedSources = this.componentSources().map((s) =>
            s.id_component_article === componentArticleId
              ? {
                  ...s,
                  selectedUnitId: s.baseUnitId,
                  availableStock: s.originalAvailableStock,
                  requiredQuantity: s.originalRequiredQuantity,
                }
              : s,
          );
          this.componentSources.set(updatedSources);
          console.error(
            '[onUnitChange] Reverted to base unit:',
            source.baseUnitId,
          );
          console.log('========================================');
        },
      });
  }

  onRequiredQuantityChange(componentArticleId: number, event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const newTotalQuantity = parseFloat(inputElement.value) || 0;
    const quantity = this.quantityToProduce();

    // Calculate the new per-unit required quantity
    const newRequiredQuantityPerUnit =
      quantity > 0 ? newTotalQuantity / quantity : 0;

    const currentSources = this.componentSources();
    const updatedSources = currentSources.map((s) =>
      s.id_component_article === componentArticleId
        ? {
            ...s,
            requiredQuantity: newRequiredQuantityPerUnit,
          }
        : s,
    );

    this.componentSources.set(updatedSources);
  }

  isStockInsufficient(
    availableStock: number,
    requiredQuantity: number,
  ): boolean {
    const quantity = this.quantityToProduce();
    const capacity = this.productionCapacity();

    // If we don't have backend data yet, don't show error
    if (!capacity || requiredQuantity === 0) {
      return false;
    }

    return availableStock < requiredQuantity * quantity;
  }

  async onProduce() {
    if (!this.isFormValid()) return;

    const article = this.compoundArticle();
    const selectedWarehouse = this.selectedWarehouse();
    const quantity = this.quantityToProduce();
    const sources = this.componentSources();

    if (!article || !selectedWarehouse) return;

    try {
      this.isProducing.set(true);

      // Build component sources with converted quantities and units when applicable
      const componentSourcesInput: ComponentWarehouseSourceInput[] =
        sources.map((source) => {
          const baseInput: ComponentWarehouseSourceInput = {
            id_warehouse: source.id_warehouse,
            id_component_article: source.id_component_article,
          };

          // If user has selected a different unit, send the converted quantity and unit
          // This ensures the backend uses the exact quantity the user sees on screen
          if (source.selectedUnitId !== source.baseUnitId) {
            baseInput.quantity = source.requiredQuantity * quantity;
            baseInput.id_unit = source.selectedUnitId;
          }

          return baseInput;
        });

      this.compoundArticlesService
        .produceCompoundArticle(
          article.id_compound_article,
          quantity,
          selectedWarehouse,
          componentSourcesInput,
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (result) => {
            this.isProducing.set(false);

            // Show success alert
            this.alertsService
              .showSuccessfullAlert(
                this.translateService.instant(
                  'inventory.compoundArticles.modals.produce.success.producedSuccessfully',
                  { quantity: this.totalProductionQuantity() },
                ),
              )
              .then((alertResult) => {
                if (alertResult.isConfirmed) {
                  // Navigate back to inventory and close all dialogs
                  this.router
                    .navigateByUrl('/table', { skipLocationChange: true })
                    .then(() => {
                      this.router.navigate(['/inventory'], {
                        queryParams: { tab: 'compound' },
                      });
                    });

                  this.dialog.closeAll();
                }
              });
          },
          error: (error) => {
            this.isProducing.set(false);
            // Show error alert
            const errorMessage =
              error?.graphQLErrors?.[0]?.message ||
              error?.message ||
              this.translateService.instant(
                'inventory.compoundArticles.modals.produce.errors.producingArticle',
              );

            this.alertsService.showErrorAlert(errorMessage);

            this.handleError(
              this.translateService.instant(
                'inventory.compoundArticles.modals.produce.errors.producingArticle',
              ),
              error,
            );
          },
        });
    } catch (error) {
      this.handleError(
        this.translateService.instant(
          'inventory.compoundArticles.modals.produce.errors.producingArticle',
        ),
        error,
      );
      this.isProducing.set(false);
    }
  }

  onCancel() {
    this.dialogRef.close({ success: false });
  }

  private handleError(message: string, error: any) {
    this.hasError.set(true);
    this.errorMessage.set(message);
    this.isLoading.set(false);
  }

  /**
   * Format quantity for display with intelligent decimal precision
   * IMPORTANT: This is ONLY for visualization - backend receives unrounded values
   *
   * Precision rules:
   * - Very small numbers (< 0.0001): up to 10 decimals
   * - Small numbers (< 1): up to 6 decimals
   * - Normal numbers (>= 1): up to 4 decimals
   * - Always removes trailing zeros
   */
  formatQuantity(value: number): number {
    if (value === 0) return 0;

    const absValue = Math.abs(value);
    let decimals: number;

    // Determine decimal precision based on magnitude
    if (absValue < 0.0001) {
      decimals = 10; // Very small numbers need high precision
    } else if (absValue < 1) {
      decimals = 6; // Small numbers
    } else {
      decimals = 4; // Normal numbers
    }

    // Round to the determined precision
    const multiplier = Math.pow(10, decimals);
    const rounded = Math.round(value * multiplier) / multiplier;

    // Remove trailing zeros by converting to string and back
    return parseFloat(rounded.toFixed(decimals));
  }
}
